import React, { useCallback, useMemo, useRef, useState } from "react";

import { 
  Skeleton, 
  Stack, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Typography,
  TablePagination
} from "@mui/material";

import { debounce } from "lodash";

import { useRoleGuardHook, useNavigate } from "hooks";
import { useClientsList, useRMList } from "queries/query-selectors/clients-query-selectors";
import { RoleName } from "constants";
import { IClientFilters, IClient, ClientTypeEnum } from "store/types/user-management";

import { Filters } from "./Filters";
// Adjust the import path for AddClientModal if necessary
import { AddClientModal } from "./AddClientModal"; 

import "./Clients.styles.scss";

const PAGE_SIZE = 50;

const EMPTY_STATE = {
  noData: {
    title: "No clients yet",
    subtitle: "Add your first client to get started."
  },
  noMatches: {
    title: "No clients match your filters.",
    subtitle: "Try adjusting your search or filters."
  },
  error: {
    title: "Couldn't load clients. Please try again."
  }
} as const;

const isAnyFilterActive = (queries: IClientFilters): boolean => {
  return Boolean((queries as Record<string, unknown>)["search"] || (queries as Record<string, unknown>)["type"] || (queries as Record<string, unknown>)["rm"]);
};

export const Clients: React.FC = () => {
  const navigate = useNavigate();

  const [queries, setQueries] = useState<IClientFilters>({ page: 1, per_page: PAGE_SIZE });
  
  // State for Add Client Modal
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  
  const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);

  const queryFilters = useMemo<IClientFilters>(() => {
    const result: IClientFilters = {};
    if ((queries as Record<string, unknown>)["rm"]) result.rm = (queries as Record<string, unknown>)["rm"] as string;
    if ((queries as Record<string, unknown>)["type"]) result.type = (queries as Record<string, unknown>)["type"] as ClientTypeEnum | "";
    if ((queries as Record<string, unknown>)["search"]) result.search = (queries as Record<string, unknown>)["search"] as string;
    result.page = queries.page;
    result.per_page = queries.per_page;
    return result;
  }, [queries]);

  const { data, isLoading, isFetching, refetchClients, isError, error } = useClientsList({
    filters: queryFilters,
    enabled: true
  });

  // Lift RM list data into the parent so the Filters popover always
  // has the latest options without making another API call internally.
  const { rms: rmOptions } = useRMList();

  const hasFilters = isAnyFilterActive(queries);
  const totalClients = data?.total ?? 0;
  const clientsData = data?.clients ?? data?.data ?? [];
  const isEmptyResult = totalClients === 0;

  const canSeeAddButton = useRoleGuardHook(
    true,
    [RoleName.Admin, RoleName.Analyst]
  ) === true;

  // Stable search handler for debounce binding.
  const handleSearchChange = useCallback(
    (value: string) => {
      setQueries((prev) => ({ ...prev, search: value ? value : undefined, page: 1 }));
    },
    [],
  );

  // Fix #1: Persist the debounced search function across renders via useRef
  // so the internal timer is not reset on every keystroke.
  const searchDebounceRef = useRef<ReturnType<typeof debounce> | null>(null);
  if (!searchDebounceRef.current) {
    searchDebounceRef.current = debounce(handleSearchChange, 300);
  }

  const handleFilterChange = useCallback((partial: Partial<IClientFilters>) => {
    setQueries((prev) => {
      const next: IClientFilters = { ...prev, page: 1 };
      for (const key of Object.keys(partial) as Array<keyof IClientFilters>) {
        const val = partial[key];
        if (val === "" || val === null || val === undefined) {
          delete (next as Record<string, unknown>)[key];
        } else {
          (next as Record<string, unknown>)[key] = val;
        }
      }
      if (next.page === 1) {
        delete (next as Record<string, unknown>)["page"];
      }
      if (next.per_page === PAGE_SIZE) {
        delete (next as Record<string, unknown>)["per_page"];
      }
      return next;
    });
  }, []);

  // Pagination handler
  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setQueries((prev) => ({ ...prev, page: newPage + 1 }));
  }, []);

  const handleRowClick = useCallback(
    (id: number) => {
      navigate({ route: "client", params: { client_id: String(id) } });
    },
    [navigate],
  );

  const handleRetry = useCallback(() => {
    refetchClients();
  }, [refetchClients]);

  // Add proper error handling for pagination
  const handlePageError = useCallback(() => {
    console.error("Pagination error occurred:", error);
    // In a real app, you might want to show a user-friendly error
  }, [error]);

  return (
      <Stack className="clients" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack className="clients__toolbar">
          <Filters
            queries={queries}
            onChange={handleFilterChange}
            isFilterOpen={isFilterPopoverOpen}
            onFilterToggle={setIsFilterPopoverOpen}
            rmOptions={rmOptions}
          />
          <div className="clients__search">
            <input
              className="clients__search-input"
              placeholder="Search clients"
              onChange={(e) => { searchDebounceRef.current?.(e.target.value); }}
              value={queries.search || ""}
            />
          </div>
          {canSeeAddButton && (
            <button 
              className="clients__add-btn" 
              onClick={() => setIsAddModalOpen(true)}
            >
              Add client
            </button>
          )}
        </Stack>

        <Stack className="clients__table-wrapper" sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
          <TableContainer className="clients__table-container">
            <Table className="clients__table" aria-labelledby="clients-table-title">
              <TableHead className="clients__table-head">
                <TableRow>
                  <TableCell sx={{ width: "240px", fontWeight: "500", fontSize: "15px", color: "#0a3142" }}>Client name</TableCell>
                  <TableCell sx={{ width: "160px", fontWeight: "500", fontSize: "15px", color: "#0a3142" }}>Type</TableCell>
                  <TableCell sx={{ width: "200px", fontWeight: "500", fontSize: "15px", color: "#0a3142" }}>RM</TableCell>
                  <TableCell sx={{ width: "120px", fontWeight: "500", fontSize: "15px", color: "#0a3142" }}>Entities</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {/* Fix #5: Use isLoading only — background refetches keep previous
                    data visible (keepPreviousData + placeholderData). Showing
                    skeletons during refetch would obscure the user's current view. */}
                {isLoading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i} className="clients__skeleton-row">
                      <TableCell>
                        <Skeleton width="180px" height="16px" />
                      </TableCell>
                      <TableCell>
                        <Skeleton width="120px" height="16px" />
                      </TableCell>
                      <TableCell>
                        <Skeleton width="150px" height="16px" />
                      </TableCell>
                      <TableCell>
                        <Skeleton width="40px" height="16px" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : isError ? (
                  <TableRow>
                    <TableCell colSpan={4} className="clients__empty-state">
                      <Typography className="clients__empty-title">{EMPTY_STATE.error.title}</Typography>
                      <button onClick={handleRetry} className="clients__retry-btn">
                        Retry
                      </button>
                    </TableCell>
                  </TableRow>
                ) : isEmptyResult && !hasFilters ? (
                  <TableRow>
                    <TableCell colSpan={4} className="clients__empty-state">
                      <Typography className="clients__empty-title">{EMPTY_STATE.noData.title}</Typography>
                      <Typography className="clients__empty-subtitle">{EMPTY_STATE.noData.subtitle}</Typography>
                      {canSeeAddButton && (
                        <button 
                          className="clients__add-btn clients__add-btn--inline"
                          onClick={() => setIsAddModalOpen(true)}
                        >
                          Add client
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : isEmptyResult && hasFilters ? (
                  <TableRow>
                    <TableCell colSpan={4} className="clients__empty-state">
                      <Typography className="clients__empty-title">{EMPTY_STATE.noMatches.title}</Typography>
                      <Typography className="clients__empty-subtitle">{EMPTY_STATE.noMatches.subtitle}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  clientsData.map((client: IClient) => (
                    <TableRow
                      key={client.id}
                      className="clients__row"
                      onClick={() => handleRowClick(client.id)}
                      sx={{ cursor: "pointer", ":hover": { backgroundColor: "#f5f7f7" } }}
                    >
                      <TableCell sx={{ color: "#26A6AE", cursor: "pointer", fontWeight: "500" }}>
                        {client.name}
                      </TableCell>
                      <TableCell sx={{ color: "#0a3142" }}>
                        {client.type ?? (
                          <Typography sx={{ color: "#898989" }}>{"\u2014"}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: "#0a3142" }}>
                        {client.rm ?? (
                          <Typography sx={{ color: "#898989" }}>{"\u2014"}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: client.entity_count === 0 ? "#898989" : "#0a3142" }}>
                        {client.entity_count}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination component - anchored to bottom */}
          {!isEmptyResult && !isLoading && !isFetching && !isError && (
            <div className="clients__pagination-wrapper" style={{ flexShrink: 0 }}>
              <TablePagination
                component="div"
                count={totalClients}
                page={(queries.page || 1) - 1}
                onPageChange={handleChangePage}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
                sx={{ borderTop: "1px solid #e0e0e0", position: "sticky", bottom: 0 }}
                onPageChange={(event, newPage) => {
                  try {
                    handleChangePage(event, newPage);
                  } catch (err) {
                    handlePageError();
                  }
                }}
              />
            </div>
          )}
        </Stack>

        {/* Add Client Modal */}
        <AddClientModal 
          open={isAddModalOpen} 
          onClose={() => setIsAddModalOpen(false)} 
          onSuccess={(clientId) => refetchClients()} 
          onNavigateToClient={(clientId) => navigate({ route: "client", params: { client_id: String(clientId) } })} 
        />
      </Stack>
    );
  };
